import { type FC, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useDemoMode } from '@/util/demo-mode';

const TRACK = [
  'relative h-6 w-11 shrink-0 cursor-pointer rounded-full',
  'transition-colors duration-[120ms] ease-[ease]',
  'aria-checked:bg-[var(--ds-color-bg-brand)]',
  'bg-[var(--ds-color-bg-tertiary)]',
  'focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--ds-color-border-secondary)]',
].join(' ');

const KNOB = [
  'pointer-events-none absolute top-0.5 left-0.5 size-5 rounded-full',
  'bg-[var(--ds-color-bg-primary)] shadow-sm',
  'transition-transform duration-[120ms] ease-[ease]',
  'motion-reduce:transition-none',
].join(' ');

/**
 * The superuser demo-mode switch.
 *
 * Carried over from the page this replaces, which the frames do not draw. It is
 * not decoration: `toggleDemoMode` is called from exactly one place in the app —
 * the old profile page — while fourteen other files read `adminFeaturesEnabled`
 * and `demoView` off it to decide what a superuser sees. Dropping it with the
 * frame would leave demo mode stuck at whatever it was last set to, with no way
 * back, as soon as the redesign flag is on. Raised with design.
 *
 * It sits in the Permissions card because that card is the superuser's, and a
 * switch over what a superuser can see belongs with the sentence about it.
 *
 * `role="switch"` with `aria-checked`, where the old one was a bare button with
 * a label and no state — a screen reader could press it but never read it.
 */
export const DemoModeSwitch: FC = () => {
  const { t } = useTranslation();
  const { demoModeEnabled, toggleDemoMode } = useDemoMode();
  const labelId = useId();

  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className="text-sm leading-5 font-medium text-[var(--ds-color-text-primary)]"
        id={labelId}
      >
        {t('profile.demo-mode')}
      </span>

      {/* Named by the span beside it. A `<label>` would not do — it associates
          with form controls, and this is a button. The knob inside is
          aria-hidden, so without this the switch reaches a screen reader with no
          name at all, which is what the old one did. */}
      <button
        aria-checked={demoModeEnabled}
        aria-labelledby={labelId}
        className={TRACK}
        id="demo-mode-toggle"
        onClick={toggleDemoMode}
        role="switch"
        type="button"
      >
        <span aria-hidden="true" className={`${KNOB} ${demoModeEnabled ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  );
};
